import UAParser from "ua-parser-js";

export const isClient = (): boolean => {
  return typeof window !== "undefined";
};

export const userAgent = () => navigator.userAgent;

export const isChrome = () => /Chrome/.test(userAgent());

export const isFirefox = () => /Firefox/.test(userAgent());

export const isSafari = () => /Safari/.test(userAgent()) && !isChrome();

export const isEdge = () => /Edg/.test(userAgent());

export const getDeviceType = (): string => {
  if (!isClient()) return "desktop";
  const parser = new UAParser(userAgent());
  const type = parser.getDevice().type;
  return type || "desktop";
};

export const isMobileDevice = (): boolean => {
  return getDeviceType() === "mobile";
};

export const isTabletDevice = (): boolean => {
  return getDeviceType() === "tablet";
};

export const isDesktopDevice = (): boolean => {
  return getDeviceType() === "desktop";
};

export const copyToClipboard = (text: string) => {
  if (isClient() && navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(text);
  }
};

type IdleCallback = () => void;

interface RequestIdleCallbackWindow {
  requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
  cancelIdleCallback?: (id: number) => void;
}

/**
 * Defer non-critical work until the browser is idle (or `timeoutMs` elapses).
 * Falls back to `setTimeout` on browsers without `requestIdleCallback` (Safari).
 * Returns a cancel function. QRWMAU-1152.
 */
export const scheduleIdle = (cb: IdleCallback, timeoutMs = 3000): (() => void) => {
  if (!isClient()) return () => {};

  const w = window as unknown as RequestIdleCallbackWindow;

  if (typeof w.requestIdleCallback === "function") {
    const handle = w.requestIdleCallback(cb, { timeout: timeoutMs });
    return () => w.cancelIdleCallback?.(handle);
  }

  const handle = window.setTimeout(cb, timeoutMs);
  return () => window.clearTimeout(handle);
};
